import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { createRawPersistStorage } from "@/lib/platform/clientStorage";
import { isRecord } from "@/lib/ui/validatedJson";

export type ShowHuntStatus = "wanted" | "found" | "skipped";
/** ``query`` is the line the collector typed. It is the only label a stop can show for a card the
 * Show can no longer describe — once the last copy sells the card read 404s, and without it a
 * vanished stop is unnameable. Optional so hunts saved before it existed still rehydrate. */
export type ShowHuntCard = { cardId: string; query?: string; status: ShowHuntStatus; };
export type ShowHunt = { inputLines: string[]; cards: ShowHuntCard[]; };

type ShowHuntsStore = {
  hunts: Record<string, ShowHunt>;
  saveHunt: (showId: string, inputLines: string[], cards: Omit<ShowHuntCard, "status">[]) => void;
  setStatus: (showId: string, cardId: string, status: ShowHuntStatus) => void;
};
type PersistedShowHunts = Pick<ShowHuntsStore, "hunts">;

const isHuntCard = (value: unknown): value is ShowHuntCard =>
  isRecord(value) && !Array.isArray(value) && typeof value.cardId === "string"
  && (value.query === undefined || typeof value.query === "string")
  && (value.status === "wanted" || value.status === "found" || value.status === "skipped");

const isHunt = (value: unknown): value is ShowHunt =>
  isRecord(value) && !Array.isArray(value)
  && Array.isArray(value.inputLines) && value.inputLines.every((line) => typeof line === "string")
  && Array.isArray(value.cards) && value.cards.every(isHuntCard);

const isHunts = (value: unknown): value is Record<string, ShowHunt> =>
  isRecord(value) && !Array.isArray(value) && Object.values(value).every(isHunt);

function decode(value: string): PersistedShowHunts | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed) || !isHunts(parsed.hunts)) return null;
    return { hunts: parsed.hunts };
  } catch {
    return null;
  }
}

const useShowHuntsStore = create<ShowHuntsStore>()(
  persist<ShowHuntsStore, [], [], PersistedShowHunts>(
    (set) => ({
      hunts: {},
      saveHunt: (showId, inputLines, cards) =>
        set((state) => {
          const previous = new Map(
            (state.hunts[showId]?.cards ?? []).map((card) => [card.cardId, card.status]),
          );
          return {
            hunts: {
              ...state.hunts,
              [showId]: {
                inputLines,
                cards: cards.map((card) => ({
                  ...card,
                  status: previous.get(card.cardId) ?? "wanted",
                })),
              },
            },
          };
        }),
      setStatus: (showId, cardId, status) =>
        set((state) => {
          const hunt = state.hunts[showId];
          if (!hunt) return state;
          return {
            hunts: {
              ...state.hunts,
              [showId]: {
                ...hunt,
                cards: hunt.cards.map((card) =>
                  card.cardId === cardId ? { ...card, status } : card
                ),
              },
            },
          };
        }),
    }),
    {
      name: "trovie.show-hunts",
      storage: createRawPersistStorage(decode, JSON.stringify),
      partialize: ({ hunts }) => ({ hunts }),
    },
  ),
);

/** Whether the on-device hunts have finished loading. ``clientStorage`` answers asynchronously on
 * a device still migrating off AsyncStorage, so a first render can legitimately see no hunt at all;
 * screens need to tell that apart from "this collector has no hunt". */
function usePersistedHunts() {
  const [hydrated, setHydrated] = useState(() => useShowHuntsStore.persist.hasHydrated());
  useEffect(() => useShowHuntsStore.persist.onFinishHydration(() => setHydrated(true)), []);
  return hydrated;
}

export function useShowHuntList(showId: string | undefined) {
  const hunt = useShowHuntsStore((state) => showId ? state.hunts[showId] : undefined);
  const save = useShowHuntsStore((state) => state.saveHunt);
  const setStatus = useShowHuntsStore((state) => state.setStatus);
  const hydrated = usePersistedHunts();
  return {
    hunt,
    hydrated,
    save: (inputLines: string[], cards: Omit<ShowHuntCard, "status">[]) => {
      if (showId) save(showId, inputLines, cards);
    },
    setStatus: (cardId: string, status: ShowHuntStatus) => {
      if (showId) setStatus(showId, cardId, status);
    },
  };
}
